import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TabDirective } from 'ngx-bootstrap/tabs';
import { takeUntil } from 'rxjs';
import { AppSyncWebSocketService } from 'src/app/appsyncwebsocketservice.service';
import { AutoUnsubscribeService } from 'src/app/services/auto-unsubscribe.service';
import { MainService } from 'src/app/services/main.service';
import { TelemetryData, WaterQualityStatus } from 'src/app/interface-model/app-interface-model';

@Component({
    selector: 'app-water-quality-manager',
    templateUrl: './water-quality-manager.component.html',
    styleUrls: ['./water-quality-manager.component.css'],
    standalone: false
})
export class WaterQualityManagerComponent extends AutoUnsubscribeService implements OnInit {

  waterQualityManagerId: string | null = null;
  selectedTab: string | undefined;
  controllerId: string = '';
  siteUuid: string = '';
  waterQualityDetails: any;
  waterQualityDetailsLoadComplete: boolean = false;
  private lastSaltStatus: WaterQualityStatus = 'unknown';
  private lastOrpStatus: WaterQualityStatus = 'unknown';
  private lastPhStatus: WaterQualityStatus = 'unknown';

  constructor(
    public mainService: MainService,
    public activatedRoute: ActivatedRoute,
    private appSyncWebSocketService: AppSyncWebSocketService,
  ) {
    super();
    this.setComponentName(this.constructor.name);
  }

  ngOnInit(): void {
    this.waterQualityManagerId = this.activatedRoute.snapshot.paramMap.get('id');

    const waterQualityContext = sessionStorage.getItem('siteContext');
    if (waterQualityContext) {
      const context = JSON.parse(waterQualityContext);
      this.controllerId = context.controllerId;
      this.siteUuid = context.siteUuid;

      this.getWaterQualityDetails();
    } else {
      this.navigateBack();
    }
  }

  onSelect(data: TabDirective): void {
    this.selectedTab = data.heading();
  }

  navigateBack(): void {
    this.mainService.goBack();
  }

  getWaterQualityDetails(): void {
    if (this.waterQualityManagerId) {
      this.mainService.getWaterQualityManager(this.controllerId, this.siteUuid, this.waterQualityManagerId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.waterQualityDetailsLoadComplete = true;
            this.waterQualityDetails = response;
            this.websocketConnection();
          },
          error: (error) => {
            this.waterQualityDetailsLoadComplete = true;
            this.waterQualityDetails = null;
          }
        });
    }
  }

  getWaterQualityIcon(): string {
    const status = this.waterQualityDetails?.spaceDetails?.waterQuality;

    return status === 'normal' ? 'assets/img/normal-status.svg' :
      status === 'needsAttention' ? 'assets/img/needs-attention.svg' :
        status === 'actionRequired' ? 'assets/img/redWarning.svg' : '';
  }

  getWaterQualityTranslationKey(status?: string): string {
    if (!status) return '';

    switch (status) {
      case 'normal':
        return 'lang.normal';
      case 'needsAttention':
        return 'lang.needs_attention';
      case 'actionRequired':
        return 'lang.action_required';
      default:
        return '';
    }
  }

  isNumber(value: any): value is number {
    return typeof value === 'number';
  }

  // Helper: Determine salt level status (ppm)
  getSaltStatus(value: number): WaterQualityStatus {
    if (value < 2500 || value > 4000) return "actionRequired";
    if (value < 3000 || value > 3500) return "needsAttention";
    return "normal";
  };

  // Helper: Determine ORP level status (mV)
  getOrpStatus(value: number): WaterQualityStatus {
    if (value < 550 || value > 900) return "actionRequired";
    if (value < 650 || value > 800) return "needsAttention";
    return "normal";
  };

  // Helper: Determine pH level status
  getpHStatus(value: number): WaterQualityStatus {
    if (value < 7 || value > 7.8) return "actionRequired";
    if (value < 7.2 || value > 7.6) return "needsAttention";
    return "normal";
  };

  getOverallWaterQuality(
    salt: WaterQualityStatus,
    orp: WaterQualityStatus,
    ph: WaterQualityStatus
  ): WaterQualityStatus {
    if (salt === "actionRequired" || orp === "actionRequired" || ph === "actionRequired")
      return "actionRequired";
    if (salt === "needsAttention" || orp === "needsAttention" || ph === "needsAttention")
      return "needsAttention";
    return "normal";
  };

  getSanitizationStatusIcon(): string {
    let status = "unknown";
    if(this.lastOrpStatus === 'unknown' || this.lastSaltStatus === 'unknown') {
      status = this.getSanitizationStatus(this.waterQualityDetails?.sanitization?.readings || []);
    }else{
      status = this.getOverallWaterQuality(this.lastSaltStatus, this.lastOrpStatus, 'unknown');
    }

    return status === 'normal' ? 'assets/img/normal-status.svg' :
      status === 'needsAttention' ? 'assets/img/needs-attention.svg' :
        status === 'actionRequired' ? 'assets/img/redWarning.svg' : '';
  }

  getBalanceStatusIcon(): string {
    let status = "unknown";
    if(this.lastPhStatus === 'unknown') {
      status = this.getpHStatus(this.waterQualityDetails?.balance?.readings?.ph?.value || 0);
    }else{
      status = this.getOverallWaterQuality('unknown', 'unknown', this.lastPhStatus);
    }

    return status === 'normal' ? 'assets/img/normal-status.svg' :
      status === 'needsAttention' ? 'assets/img/needs-attention.svg' :
        status === 'actionRequired' ? 'assets/img/redWarning.svg' : '';
  }

  hasTruSenseBalanceEquipment(): boolean {
    return this.waterQualityDetails?.balance?.balanceEquipment?.some((equipment: { type?: string }) => equipment?.type === 'truSense') ?? false;
  }

  getSanitizationStatus(readings: any[]): WaterQualityStatus {

    // If there are no readings, the sanitization status is unknown.  
    if (!readings || readings.length === 0) {  
      return 'unknown';  
    }  

    const statuses: WaterQualityStatus[] = readings.map(reading => {  
      switch (reading.type) {  
        case 'salt':  
          return this.getSaltStatus(reading.value);  

        case 'orp':  
          return this.getOrpStatus(reading.value);  

        default:  
          // Unsupported reading types should not be treated as "normal".  
          return 'unknown';  
      }  
    });  

    if (statuses.includes('actionRequired')) {  
      return 'actionRequired';  
    }  

    if (statuses.includes('needsAttention')) {  
      return 'needsAttention';  
    }  

    // If all statuses are unknown (i.e., no meaningful data), propagate "unknown".  
    const hasKnownStatus = statuses.some(status =>  
      status === 'normal' || status === 'needsAttention' || status === 'actionRequired'  
    );  

    if (!hasKnownStatus) {  
      return 'unknown';  
    }  

    return 'normal';
  }

  websocketConnection() {
    this.appSyncWebSocketService.connectWebSocket(this.controllerId);
    this.appSyncWebSocketService.getTelemetryData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((telemetry: TelemetryData | null) => {
        if (!telemetry) return;
       
        const validCategories = ['sanitizer', 'orpSensor', 'phSensor'];

        if (!validCategories.includes(telemetry.category)) {
          return;
        }

        switch (telemetry.category) {
          case 'sanitizer':
            this.lastSaltStatus = telemetry.value
              ? this.getSaltStatus(telemetry.value)
              : 'unknown';
            break;

          case 'orpSensor':
            this.lastOrpStatus = telemetry.value
              ? this.getOrpStatus(telemetry.value)
              : 'unknown';
            break;

          case 'phSensor':
            const phValue = telemetry.value ? telemetry.value / 10 : null;
            this.lastPhStatus = phValue !== null
              ? this.getpHStatus(phValue)
              : 'unknown';
            if (phValue !== null && this.waterQualityDetails?.balance?.readings?.ph) {
              this.waterQualityDetails.balance.readings.ph.value = phValue;
            }
            break;
        }

        this.waterQualityDetails.spaceDetails.waterQuality =
          this.getOverallWaterQuality(
            this.lastSaltStatus,
            this.lastOrpStatus,
            this.lastPhStatus
          );
        this.waterQualityDetails.spaceDetails.lastChecked = telemetry.timestamp;
      });
  }

  override ngOnDestroy(): void {
    this.appSyncWebSocketService.disconnect();
    super.ngOnDestroy();
  }
}
